import { decodeInterleavedRle } from "./rle";

const MAGIC = "TRDP";
const SUPPORTED_VERSION = 2;
const HEADER_SIZE = 20;

const FRAME_KIND_UPDATE = 0;
const FRAME_KIND_KEYFRAME = 1;

const RECTANGLE_ENTRY_SIZE = 18;
const BITMAP_COMPRESSION = 0x0001;

export interface BitmapRectangle {
  destLeft: number;
  destTop: number;
  width: number;
  height: number;
  compressed: boolean;
  data: Uint8Array;
}

export type Frame =
  | { kind: "update"; elapsedMs: number; rectangles: BitmapRectangle[] }
  | { kind: "keyframe"; elapsedMs: number; png: Uint8Array };

export interface Recording {
  width: number;
  height: number;
  bitsPerPixel: number;
  startedAt: Date;
  durationMs: number;
  frames: Frame[];
}

function isGzip(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}

async function gunzip(bytes: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  if (typeof DecompressionStream === "undefined") {
    throw new Error("This browser cannot decompress recordings.");
  }

  const stream = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream("gzip"));

  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export async function parseRecording(source: ArrayBuffer): Promise<Recording> {
  let bytes = new Uint8Array(source);

  if (isGzip(bytes)) {
    bytes = await gunzip(bytes);
  }

  if (bytes.length < HEADER_SIZE) {
    throw new Error("Recording is too short to contain a header.");
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const magic = String.fromCharCode(...bytes.subarray(0, 4));

  if (magic !== MAGIC) {
    throw new Error(`Not a Tendra RDP recording (magic "${magic}").`);
  }

  const version = view.getUint16(4, true);

  if (version !== SUPPORTED_VERSION) {
    throw new Error(`Unsupported recording version ${version}.`);
  }

  const width = view.getUint16(6, true);
  const height = view.getUint16(8, true);
  const bitsPerPixel = view.getUint16(10, true);
  const startedAt = new Date(Number(view.getBigUint64(12, true)));

  const frames: Frame[] = [];
  let at = HEADER_SIZE;

  while (at < bytes.length) {
    const kind = bytes[at];
    if (kind === undefined || at + 5 > bytes.length) break;

    const elapsedMs = view.getUint32(at + 1, true);

    if (kind === FRAME_KIND_KEYFRAME) {
      if (at + 9 > bytes.length) break;

      const size = view.getUint32(at + 5, true);
      const start = at + 9;
      if (start + size > bytes.length) break;

      frames.push({
        kind: "keyframe",
        elapsedMs,
        png: bytes.subarray(start, start + size),
      });

      at = start + size;

      continue;
    }

    if (kind !== FRAME_KIND_UPDATE) {
      throw new Error(`Unknown frame kind ${kind} at byte ${at}.`);
    }

    if (at + 7 > bytes.length) break;

    const count = view.getUint16(at + 5, true);
    let cursor = at + 7;
    const rectangles: BitmapRectangle[] = [];
    let truncated = false;

    for (let i = 0; i < count; i += 1) {
      if (cursor + RECTANGLE_ENTRY_SIZE > bytes.length) {
        truncated = true;
        break;
      }

      const dataLength = view.getUint16(cursor + 16, true);
      const dataStart = cursor + RECTANGLE_ENTRY_SIZE;

      if (dataStart + dataLength > bytes.length) {
        truncated = true;
        break;
      }

      rectangles.push({
        destLeft: view.getUint16(cursor, true),
        destTop: view.getUint16(cursor + 2, true),
        width: view.getUint16(cursor + 8, true),
        height: view.getUint16(cursor + 10, true),
        compressed:
          (view.getUint16(cursor + 12, true) & BITMAP_COMPRESSION) !== 0,
        data: bytes.subarray(dataStart, dataStart + dataLength),
      });

      cursor = dataStart + dataLength;
    }

    if (truncated) break;

    frames.push({ kind: "update", elapsedMs, rectangles });
    at = cursor;
  }

  const last = frames.at(-1);

  return {
    width,
    height,
    bitsPerPixel,
    startedAt,
    durationMs: last?.elapsedMs ?? 0,
    frames,
  };
}

function writeRgb565(out: Uint8ClampedArray, at: number, pixel: number): void {
  const r = (pixel >> 11) & 0x1f;
  const g = (pixel >> 5) & 0x3f;
  const b = pixel & 0x1f;

  out[at] = (r * 255 + 15) / 31;
  out[at + 1] = (g * 255 + 31) / 63;
  out[at + 2] = (b * 255 + 15) / 31;
  out[at + 3] = 255;
}

export class Framebuffer {
  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  clear(width: number, height: number): void {
    this.ctx.fillStyle = "#000000";
    this.ctx.fillRect(0, 0, width, height);
  }

  applyRectangle(rect: BitmapRectangle): void {
    const { width, height } = rect;

    if (width === 0 || height === 0) return;

    let pixels: Uint16Array;

    if (rect.compressed) {
      pixels = decodeInterleavedRle(rect.data, width, height);
    } else {
      if (rect.data.length < width * height * 2) {
        throw new Error(
          `Uncompressed rectangle is short (${rect.data.length} bytes for ${width}x${height}).`,
        );
      }

      pixels = new Uint16Array(width * height);
      const source = new DataView(
        rect.data.buffer,
        rect.data.byteOffset,
        rect.data.byteLength,
      );

      for (let i = 0; i < pixels.length; i += 1) {
        pixels[i] = source.getUint16(i * 2, true);
      }
    }

    const image = this.ctx.createImageData(width, height);

    for (let y = 0; y < height; y += 1) {
      const destRow = height - 1 - y;

      for (let x = 0; x < width; x += 1) {
        writeRgb565(
          image.data,
          (destRow * width + x) * 4,
          pixels[y * width + x] ?? 0,
        );
      }
    }

    this.ctx.putImageData(image, rect.destLeft, rect.destTop);
  }

  async applyKeyframe(png: Uint8Array): Promise<void> {
    const bitmap = await createImageBitmap(
      new Blob([png as BlobPart], { type: "image/png" }),
    );

    try {
      this.ctx.drawImage(bitmap, 0, 0);
    } finally {
      bitmap.close();
    }
  }

  async applyFrame(frame: Frame): Promise<void> {
    if (frame.kind === "keyframe") {
      await this.applyKeyframe(frame.png);

      return;
    }

    for (const rect of frame.rectangles) {
      this.applyRectangle(rect);
    }
  }
}

export function seekStart(frames: Frame[], targetMs: number): number {
  for (let i = frames.length - 1; i >= 0; i -= 1) {
    const frame = frames[i];

    if (frame && frame.kind === "keyframe" && frame.elapsedMs <= targetMs) {
      return i;
    }
  }

  return 0;
}
